import type {
  DatabaseSelectResponse,
  Enduser,
  EnduserInsurance,
  FormField,
  FormResponseValueAnswer,
} from "@tellescope/types-models";

const INCHES_PER_FOOT = 12;

const isInsurance = (value: unknown): value is EnduserInsurance =>
  !!value && typeof value === "object" && !Array.isArray(value);

const isDatabaseSelectResponse = (value: unknown): value is DatabaseSelectResponse =>
  !!value && typeof value === "object" && "text" in value && typeof value.text === "string";

const asString = (value: unknown): string | undefined => {
  if (typeof value === "string") return value || undefined;
  if (typeof value === "number") return String(value);
  return undefined;
};

const asNumber = (value: unknown): number | undefined => {
  if (typeof value === "number") return Number.isNaN(value) ? undefined : value;
  if (typeof value === "string" && value.trim() !== "") {
    const parsed = Number(value);
    return Number.isNaN(parsed) ? undefined : parsed;
  }
  return undefined;
};

const quantityValue = (value: unknown): number | undefined =>
  value && typeof value === "object" && "value" in value ? asNumber(value.value) : asNumber(value);

const readEnduserValue = (enduser: Partial<Enduser>, intakeField: string): unknown => {
  switch (intakeField) {
    case "insurance.details":
      return enduser.insurance;
    case "insuranceSecondary.details":
      return enduser.insuranceSecondary;
    case "email":
      return enduser.email;
    case "phone":
      return enduser.phone;
    case "fname":
      return enduser.fname;
    case "lname":
      return enduser.lname;
    case "dateOfBirth":
      return enduser.dateOfBirth;
    case "gender":
      return enduser.gender;
    case "height":
      return enduser.height;
    case "weight":
      return enduser.weight;
    case "Address":
      return {
        addressLineOne: enduser.addressLineOne,
        addressLineTwo: enduser.addressLineTwo,
        city: enduser.city,
        state: enduser.state,
        zipCode: enduser.zipCode,
        zipPlusFour: enduser.zipPlusFour,
      };
    default:
      return enduser.fields?.[intakeField];
  }
};

const compactAddress = (value: unknown): Record<string, string> | undefined => {
  if (!value || typeof value !== "object") return undefined;
  const entries = Object.entries(value).reduce<Record<string, string>>((acc, [key, entry]) => {
    const asText = asString(entry);
    if (asText !== undefined) acc[key] = asText;
    return acc;
  }, {});
  return Object.keys(entries).length ? entries : undefined;
};

export const resolvePrepopulatedValue = (
  field: Pick<FormField, "type" | "intakeField">,
  enduser: Partial<Enduser> | undefined
): FormResponseValueAnswer["value"] | undefined => {
  if (!enduser || !field.intakeField) return undefined;

  const stored = readEnduserValue(enduser, field.intakeField);
  if (stored === undefined || stored === null || stored === "") return undefined;

  switch (field.type) {
    case "Height": {
      const totalInches = quantityValue(stored);
      if (totalInches === undefined) return undefined;
      return { feet: Math.floor(totalInches / INCHES_PER_FOOT), inches: totalInches % INCHES_PER_FOOT };
    }
    case "number":
      return quantityValue(stored);
    case "multiple_choice":
    case "Dropdown": {
      if (Array.isArray(stored)) return stored.filter((entry): entry is string => typeof entry === "string");
      const single = asString(stored);
      return single === undefined ? undefined : [single];
    }
    case "Address":
      return compactAddress(stored);
    case "Insurance":
      return isInsurance(stored) ? stored : undefined;
    case "Database Select": {
      if (Array.isArray(stored)) return stored.filter(isDatabaseSelectResponse);
      const single = asString(stored);
      return single === undefined ? undefined : [{ text: single }];
    }
    case "rating":
      return asNumber(stored);
    default:
      return asString(stored);
  }
};
